import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, Log } from 'oidc-client';
import { environment } from '../../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { AuthenticationType, OidcFactory } from './oidc.factory';

export class OidcBaseService {

  constructor(userManagerSettings: UserManagerSettings, protected _router: Router, protected _sessionService: SessionService) {
    Log.level = Log.DEBUG;
    Log.logger = console;

    this.userManagerSettings = userManagerSettings;

    this.manager = new UserManager(this.userManagerSettings);

    this.manager.events.addSilentRenewError(() => {
      console.log('Failed to renew session. Logging out.');
      this._router.navigate(['/public/logout'], { queryParams: { ssoLogout: true } });
    });
    this.manager.events.addAccessTokenExpired(() => {
      console.log('Users session has expired. Logging out.');
      this._router.navigate(['/public/logout'], { queryParams: { ssoLogout: true } });
    });
    this.manager.events.addUserLoaded((user) => {
      console.log('Users session established or renewed.');
      this._sessionService.setAuthenticationType(this.authType, { idToken: user.id_token, accessToken: user.access_token });
    });
    this.manager.events.addUserSessionChanged(() => {
      console.log('Users session has changed.');
    })
  }
  private userManagerSettings: UserManagerSettings;
  private manager: UserManager;
  protected authType: AuthenticationType;

  static parseUrlFragment(value, delimiter = '#') {
    let idx = value.lastIndexOf(delimiter);
    if (idx >= 0) {
      value = value.substr(idx + 1);
    }

    if (delimiter === '?') {
      // if we're doing query, then strip off hash fragment before we parse
      idx = value.indexOf('#');
      if (idx >= 0) {
        value = value.substr(0, idx);
      }
    }

    const params = {};
    const regex = /([^&=]+)=([^&]*)/g;
    let m = regex.exec(value)

    let counter = 0;
    while (m) {
      params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
      if (counter++ > 50) {
        return {
          error: 'Response exceeded expected number of parameters'
        };
      }
      m = regex.exec(value);
    }
    return params;
  }

  static decodeToken(token) {
    const jwtHelper = new JwtHelperService();

    const parts = token.split('.');

    const header = jwtHelper.urlBase64Decode(parts[0]);
    if (!header) {
      throw new Error('Cannot decode the token header.');
    }

    const payload = jwtHelper.urlBase64Decode(parts[1]);
    if (!header) {
      throw new Error('Cannot decode the token payload.');
    }

    return { header: JSON.parse(header), payload: JSON.parse(payload) };
  }

  getManager(): UserManager {
    return this.manager;
  }

  setSigningState(url) {
    try {
      const sr: any = OidcBaseService.parseUrlFragment(url);
      const idToken = OidcBaseService.decodeToken(sr.id_token);
      const storageState = {
        id: sr.state,
        data: sr.data,
        created: sr.created,
        nonce: idToken.payload.nonce,
        redirect_uri: url,
        authority: idToken.payload.iss,
        client_id: idToken.payload.aud
      };
      // console.log("setSigningState", url, idToken, storageState);
      localStorage.setItem(`oidc.${storageState.id}`, JSON.stringify(storageState));
      return idToken;
    } catch (err) {
      // No token can be retrieved
      return undefined;
    }
  }
}

@Injectable()
@OidcFactory.register(AuthenticationType.SSOJWT)
export class OidcConnectWiseService extends OidcBaseService {

  constructor(protected _router: Router, protected _sessionService: SessionService) {
    super(OidcConnectWiseService.getConfig(), _router, _sessionService);

    this.authType = AuthenticationType.SSOJWT;
  }

  private static getConfig() {
    return {
      authority: environment.cwSSO.authority,
      client_id: environment.cwSSO.clientId,
      redirect_uri: environment.cwSSO.redirectUri,
      silent_redirect_uri: environment.cwSSO.silentRedirectUri,
      popup_redirect_uri: environment.cwSSO.popupRedirectUri,
      response_type: 'id_token token',
      scope: 'openid profile',
      loadUserInfo: false,
      monitorSession: false,
      automaticSilentRenew: true
    };
  }
}

@Injectable()
@OidcFactory.register(AuthenticationType.SBAM)
export class OidcSbamService extends OidcBaseService {

  constructor(protected _router: Router, protected _sessionService: SessionService) {
    super(OidcSbamService.getConfig(), _router, _sessionService);

    this.authType = AuthenticationType.SBAM;
  }

  private static getConfig() {
    return {
      authority: environment.sbamSSO.authority,
      client_id: environment.sbamSSO.clientId,
      redirect_uri: environment.sbamSSO.redirectUri,
      silent_redirect_uri: environment.sbamSSO.silentRedirectUri,
      response_type: 'id_token token',
      scope: 'openid profile',
      loadUserInfo: false,
      monitorSession: false,
      automaticSilentRenew: true
    };
  }
}